import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const SearchBar = ({ className = '', variant = 'default', onSearch }) => {
  const [query, setQuery] = useState('');
  const [location, setLocation] = useState('');
  const [listingType, setListingType] = useState('all');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (location) params.set('location', location);
    if (listingType && listingType !== 'all') params.set('type', listingType);
    
    if (onSearch) {
      onSearch({ query, location, listingType });
    } else {
      navigate(`/search?${params.toString()}`);
    }
  };

  const isHero = variant === 'hero';

  return (
    <form
      onSubmit={handleSearch}
      className={`${className} ${isHero ? 'w-full max-w-4xl' : ''}`}
      data-testid="search-form"
    >
      <div className={`
        flex flex-col md:flex-row gap-2 md:gap-0
        ${isHero ? 'bg-white rounded-2xl shadow-xl p-2 md:p-3 border border-gray-100' : 'bg-white rounded-xl p-2 border border-gray-200'}
      `}>
        {/* Search Input */}
        <div className="flex-1 flex items-center gap-2 px-3">
          <Search className="w-5 h-5 text-gray-400 shrink-0" />
          <Input
            type="text"
            placeholder="Search doctors, clinics, pharmacies..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="border-0 shadow-none focus-visible:ring-0 px-0 text-base"
            data-testid="search-query-input"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Divider */}
        <div className="hidden md:block w-px bg-gray-200 my-2" />

        {/* Location Input */}
        <div className="flex-1 flex items-center gap-2 px-3">
          <MapPin className="w-5 h-5 text-gray-400 shrink-0" />
          <Input
            type="text"
            placeholder="City or ZIP code"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="border-0 shadow-none focus-visible:ring-0 px-0 text-base"
            data-testid="search-location-input"
          />
          {location && (
            <button
              type="button"
              onClick={() => setLocation('')}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Type Selector (for hero variant) */}
        {isHero && (
          <>
            <div className="hidden md:block w-px bg-gray-200 my-2" />
            <div className="px-3">
              <Select value={listingType} onValueChange={setListingType}>
                <SelectTrigger className="border-0 shadow-none focus:ring-0 w-[140px]" data-testid="search-type-select">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="facility">Facilities</SelectItem>
                  <SelectItem value="specialist">Specialists</SelectItem>
                  <SelectItem value="pharmacy">Pharmacies</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        {/* Search Button */}
        <Button
          type="submit"
          className={`
            bg-teal-700 hover:bg-teal-800 text-white rounded-full
            ${isHero ? 'px-8 py-6' : 'px-6'}
          `}
          data-testid="search-submit-btn"
        >
          <Search className="w-5 h-5 md:mr-2" />
          <span className="hidden md:inline">Search</span>
        </Button>
      </div>
    </form>
  );
};

export default SearchBar;
